import { isGspChannelSessionActiveForProduct } from '@theme/gsp-channel-session';

// Retain Shopify/Yotpo metadata; override only identified variant offer prices.
// This also handles app schemas inserted after the initial document is parsed.
function refreshProductSchemas() {
  const products = new Map();
  for (const element of document.querySelectorAll('script[data-product-schema-pricing]')) {
    try {
      const data = JSON.parse(element.textContent);
      if (data.handle && data.variants?.length) products.set(data.handle, data);
    } catch { /* Ignore an incomplete section response. */ }
  }
  if (!products.size) return;

  for (const meta of document.querySelectorAll('meta[data-product-price-meta]')) {
    const product = [...products.values()].find((entry) => entry.id === meta.dataset.productPriceMeta);
    const variant = product?.variants.find((entry) => entry.id === meta.dataset.variantId);
    if (!variant) continue;
    const amount = variant.eligible && isGspChannelSessionActiveForProduct(product.id)
      ? variant.gsp : variant.regular;
    if (Number.isFinite(amount) && amount >= 0) meta.setAttribute('content', amount.toFixed(2));
  }

  function update(node, parent = {}) {
    if (!node || typeof node !== 'object') return;
    if (Array.isArray(node)) {
      node.forEach((entry) => update(entry, parent));
      return;
    }
    const types = Array.isArray(node['@type']) ? node['@type'] : [node['@type']];
    if (types.includes('Offer')) {
      try {
        const url = new URL(node.url || node['@id'] || parent.url || parent['@id'], location.origin);
        if (url.origin !== location.origin) return;
        const handle = url.pathname.match(/\/products\/([^/]+)\/?$/)?.[1];
        const product = products.get(handle);
        if (!product) return;
        const variantId = url.searchParams.get('variant');
        const sku = node.sku || parent.sku;
        const matches = product.variants.filter((variant) => variantId
          ? variant.id === variantId
          : sku ? variant.sku === sku : product.variants.length === 1);
        if (matches.length !== 1) return;
        const variant = matches[0];
        const amount = variant.eligible && isGspChannelSessionActiveForProduct(product.id)
          ? variant.gsp : variant.regular;
        if (Number.isFinite(amount) && amount >= 0) node.price = amount.toFixed(2);
      } catch { /* Unrecognized offers remain untouched. */ }
      return;
    }
    for (const value of Object.values(node)) update(value, node);
  }

  for (const script of document.querySelectorAll('script[type="application/ld+json"]')) {
    try {
      const data = JSON.parse(script.textContent);
      const before = JSON.stringify(data);
      update(data);
      const after = JSON.stringify(data);
      if (before !== after) script.textContent = after;
    } catch { /* Do not rewrite malformed or non-JSON app content. */ }
  }
}

let scheduled = false;
function schedule() {
  if (scheduled) return;
  scheduled = true;
  queueMicrotask(() => {
    scheduled = false;
    refreshProductSchemas();
  });
}
new MutationObserver((mutations) => {
  if (mutations.some((mutation) =>
    mutation.target.nodeName === 'SCRIPT' ||
    [...mutation.addedNodes].some((node) => node.nodeType === 1 &&
      (node.nodeName === 'SCRIPT' || node.querySelector?.('script'))))) schedule();
}).observe(document.documentElement, { childList: true, subtree: true, characterData: true });
window.addEventListener('pageshow', schedule);
document.addEventListener('variant:update', (event) => {
  const variant = event.detail?.resource;
  if (!variant?.id) return;
  for (const meta of document.querySelectorAll('meta[data-product-price-meta]')) {
    if (String(event.detail.data?.productId) === meta.dataset.productPriceMeta) {
      meta.dataset.variantId = String(variant.id);
    }
  }
  schedule();
});
document.addEventListener('visibilitychange', schedule);
setInterval(schedule, 60000);
refreshProductSchemas();
